/**
 * sura-wishlist.js
 *
 * localStorage-backed wishlist for the Sura theme.
 *
 * Exposes window.SuraWishlist (store) and three custom elements:
 *   <sura-wishlist-button>  toggle, used in product cards and on the product page
 *   <sura-wishlist-count>   badge in the header
 *   <sura-wishlist-page>    grid renderer for templates/page.wishlist.json
 *
 * Safe to declare in more than one section — the IIFE and the
 * customElements.get() guards make a second load a no-op.
 */
(function () {
  'use strict';

  if (window.SuraWishlist) return;

  var STORAGE_KEY = 'sura:wishlist:v1';
  var LEGACY_KEY = 'm-wishlist-products'; // Minimog's own key
  var CHANGE_EVENT = 'sura:wishlist:change';
  var BODY_CLASS = 'sura-wishlist-has-items';

  /* ---------------------------------------------------------------- store */

  function parse(raw) {
    try {
      var value = JSON.parse(raw);
      if (!Array.isArray(value)) return [];
      return value.filter(function (handle) {
        return typeof handle === 'string' && handle.length > 0;
      });
    } catch (error) {
      return [];
    }
  }

  function unique(handles) {
    var seen = {};
    return handles.filter(function (handle) {
      if (seen[handle]) return false;
      seen[handle] = true;
      return true;
    });
  }

  function read() {
    return unique(parse(localStorage.getItem(STORAGE_KEY)));
  }

  function broadcast(handles, meta) {
    document.body && document.body.classList.toggle(BODY_CLASS, handles.length > 0);
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: Object.assign({ handles: handles, count: handles.length }, meta || {})
      })
    );
  }

  function write(handles, meta) {
    var next = unique(handles);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      // Private mode or quota exceeded. The page stays usable for this session;
      // we just can't persist. Deliberately silent.
    }
    broadcast(next, meta);
    return next;
  }

  var SuraWishlist = {
    STORAGE_KEY: STORAGE_KEY,
    CHANGE_EVENT: CHANGE_EVENT,

    get handles() {
      return read();
    },

    get count() {
      return read().length;
    },

    has: function (handle) {
      return read().indexOf(handle) > -1;
    },

    add: function (handle) {
      if (!handle || this.has(handle)) return read();
      return write(read().concat(handle), { handle: handle, added: true });
    },

    remove: function (handle) {
      if (!handle) return read();
      return write(
        read().filter(function (item) {
          return item !== handle;
        }),
        { handle: handle, added: false }
      );
    },

    toggle: function (handle) {
      return this.has(handle) ? this.remove(handle) : this.add(handle);
    },

    /** Drop handles that no longer resolve to a live product. */
    prune: function (handles) {
      if (!handles || !handles.length) return read();
      return write(
        read().filter(function (item) {
          return handles.indexOf(item) === -1;
        }),
        { source: 'prune' }
      );
    },

    clear: function () {
      return write([], { source: 'clear' });
    }
  };

  // One-time import of anything saved by the vendor wishlist, so testing
  // sessions and any live saves aren't lost when the new store takes over.
  if (localStorage.getItem(STORAGE_KEY) === null) {
    var legacy = parse(localStorage.getItem(LEGACY_KEY));
    if (legacy.length) write(legacy, { source: 'migration' });
  }

  // Keep other open tabs in step.
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) broadcast(read(), { source: 'storage' });
  });

  window.SuraWishlist = SuraWishlist;

  /* ------------------------------------------------------------- elements */

  function define(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.control = this.querySelector('button');
      this.handle = this.dataset.productHandle;
      if (!this.control || !this.handle) return;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);

      this.control.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.onChange);

      this.sync();
      // Defer the transition until after first paint so the fill
      // doesn't animate on load.
      requestAnimationFrame(() => this.classList.add('is-ready'));
    }

    disconnectedCallback() {
      if (this.control) this.control.removeEventListener('click', this.onClick);
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    onClick(event) {
      // Product cards are usually wrapped in an <a> — stop both.
      event.preventDefault();
      event.stopPropagation();
      SuraWishlist.toggle(this.handle);
    }

    onChange(event) {
      var detail = event.detail || {};
      // Only re-sync when this handle changed, or on a bulk event.
      if (detail.handle && detail.handle !== this.handle) return;
      this.sync();
    }

    sync() {
      var active = SuraWishlist.has(this.handle);
      var label = active
        ? this.dataset.labelRemove || 'Remove from wishlist'
        : this.dataset.labelAdd || 'Add to wishlist';

      this.classList.toggle('is-active', active);
      this.control.setAttribute('aria-pressed', String(active));
      this.control.setAttribute('aria-label', label);

      var text = this.querySelector('[data-wishlist-text]');
      if (text) text.textContent = label;
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.onChange = this.render.bind(this);
      document.addEventListener(CHANGE_EVENT, this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    render() {
      var count = SuraWishlist.count;
      this.textContent = count > 99 ? '99+' : String(count);
      this.hidden = count === 0;
      this.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    }
  }

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.loading = this.querySelector('[data-wishlist-loading]');
      this.view = this.dataset.cardView || 'sura-card';
      this.token = 0;

      this.onChange = this.onChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    onChange(event) {
      var detail = event.detail || {};
      // Our own prune fires a change event — don't loop on it.
      if (detail.source === 'prune') return;

      // A removal on this page only needs the one node taken out,
      // not a full refetch of everything else.
      if (detail.handle && detail.added === false && this.grid) {
        var node = this.grid.querySelector(
          '[data-wishlist-item="' + CSS.escape(detail.handle) + '"]'
        );
        if (node) {
          node.remove();
          if (!this.grid.children.length) this.showEmpty();
          return;
        }
      }

      this.render();
    }

    async render() {
      var token = ++this.token;
      var handles = SuraWishlist.handles;

      if (!handles.length) return this.showEmpty();

      this.setState('loading');

      var results = await Promise.all(
        handles.map((handle) => this.fetchCard(handle))
      );

      // A newer render started while we were fetching — discard this one.
      if (token !== this.token) return;

      var fragment = document.createDocumentFragment();
      var missing = [];

      // Iterate `handles`, not `results`, so stored order is preserved.
      results.forEach((result) => {
        if (!result.html) {
          missing.push(result.handle);
          return;
        }
        fragment.appendChild(this.buildItem(result.handle, result.html));
      });

      this.grid.innerHTML = '';
      this.grid.appendChild(fragment);

      if (!this.grid.children.length) {
        this.showEmpty();
      } else {
        this.setState('ready');
      }

      // Products that 404'd have been deleted or unpublished. Drop them
      // so the list doesn't keep retrying dead handles on every visit.
      if (missing.length) SuraWishlist.prune(missing);
    }

    buildItem(handle, html) {
      var item = document.createElement('div');
      item.className = 'sura-wishlist__item';
      item.setAttribute('data-wishlist-item', handle);
      item.innerHTML = html;
      item.appendChild(this.buildRemoveButton(handle));
      return item;
    }

    buildRemoveButton(handle) {
      var label = this.dataset.labelRemove || 'Remove from wishlist';
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'sura-wishlist__remove';
      button.setAttribute('aria-label', label);
      button.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="M6 18 18 6M6 6l12 12"/></svg>';
      button.addEventListener('click', function () {
        SuraWishlist.remove(handle);
      });
      return button;
    }

    async fetchCard(handle) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url = root + 'products/' + encodeURIComponent(handle) + '?view=' + this.view;

      try {
        var response = await fetch(url);
        if (!response.ok) return { handle: handle, html: '' };
        var html = (await response.text()).trim();
        return { handle: handle, html: html };
      } catch (error) {
        return { handle: handle, html: '' };
      }
    }

    setState(state) {
      if (this.loading) this.loading.hidden = state !== 'loading';
      if (this.grid) this.grid.hidden = state !== 'ready';
      if (this.empty) this.empty.hidden = state !== 'empty';
    }

    showEmpty() {
      if (this.grid) this.grid.innerHTML = '';
      this.setState('empty');
    }
  }

  define('sura-wishlist-button', WishlistButton);
  define('sura-wishlist-count', WishlistCount);
  define('sura-wishlist-page', WishlistPage);

  // Set the body class on first load so header styling is correct
  // before any interaction happens.
  if (document.body) {
    document.body.classList.toggle(BODY_CLASS, SuraWishlist.count > 0);
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      document.body.classList.toggle(BODY_CLASS, SuraWishlist.count > 0);
    });
  }
})();
